import type { TFunction } from "i18next";
import { isApiError } from "../../api/client.js";

const CONTACT_ERROR_KEYS: Record<string, string> = {
  CONTACT_PHONE_MISSING_COUNTRY: "contacts.errors.missingCountry",
  ORGANIZATION_HAS_NO_ACTIVE_OWNER: "organization.platformAdmin.noActiveOwner",
  CONTACT_INVALID_PHONE: "contacts.errors.invalidPhone",
  CONTACT_DUPLICATE_PHONE: "contacts.errors.duplicatePhone",
  CONTACT_DUPLICATE_EMAIL: "contacts.errors.duplicateEmail",
  CONTACT_RESTORE_PHONE_CONFLICT: "contacts.errors.restorePhoneConflict",
  CONTACT_PHONE_RESTORE_AMBIGUOUS: "contacts.errors.phoneRestoreAmbiguous",
  CONTACT_WHATSAPP_PHONE_LOCKED: "contacts.errors.whatsappPhoneLocked",
  CONTACT_WHATSAPP_PHONE_CONFLICT: "contacts.errors.whatsappPhoneConflict",
  CONTACT_PLATFORM_PROFILE_CONFLICT: "contacts.errors.platformProfileConflict",
  CONTACT_MERGE_CONFLICT: "contacts.errors.mergeConflict",
  CONTACT_MERGE_BIRTHDAY_CHOICE_REQUIRED: "contacts.errors.mergeBirthdayChoiceRequired",
  CONTACT_INVALID_BIRTHDAY: "contacts.errors.invalidBirthday",
  CONTACT_ALREADY_LINKED: "contacts.errors.alreadyLinked",
  CONTACT_PLATFORM_PROFILE_NOT_FOUND: "contacts.errors.profileNotFound",
  NO_CONTACT_TO_RESTORE: "contacts.errors.noContactToRestore",
  VALIDATION_FAILED: "contacts.errors.validation",
  VALIDATION_ERROR: "contacts.errors.validation",
  NOT_FOUND: "contacts.errors.notFound",
  FORBIDDEN: "contacts.errors.forbidden",
  CONTACT_IMPORT_TOO_MANY_ROWS: "contacts.import.errors.TOO_MANY_ROWS",
  CONTACT_IMPORT_INVALID_MAPPING: "contacts.import.errors.INVALID_MAPPING",
  CONTACT_IMPORT_EMPTY: "contacts.import.errors.EMPTY_CSV",
  CONTACT_IMPORT_INVALID_GROUPS: "contacts.import.errors.INVALID_GROUPS",
  CONTACT_IMPORT_FILE_TOO_LARGE: "contacts.import.errors.FILE_TOO_LARGE",
  CONTACT_IMPORT_INVALID_FILE_TYPE: "contacts.import.errors.INVALID_FILE_TYPE",
  CONTACT_IMPORT_JOB_NOT_FOUND: "contacts.import.error",
  CONTACT_IMPORT_JOB_NOT_CONFIRMABLE: "contacts.import.error",
  OBJECT_STORAGE_UNAVAILABLE: "contacts.import.errors.UPLOAD_UNAVAILABLE",
  CONTACT_LIMIT_REACHED: "contacts.errors.limitReached.message",
};

export function readApplicationErrorCode(error: unknown): string | undefined {
  if (isApiError(error)) {
    if (error.status === 0) return undefined;
    return error.code;
  }
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    return typeof code === "string" && code.length > 0 ? code : undefined;
  }
  return undefined;
}

export function isContactLimitReachedError(error: unknown): boolean {
  return readApplicationErrorCode(error) === "CONTACT_LIMIT_REACHED";
}

export function contactLimitFeedback(
  t: TFunction,
  context: "create" | "import",
): { title: string; message: string } {
  if (context === "import") {
    return {
      title: t("contacts.errors.limitReached.importTitle"),
      message: t("contacts.errors.limitReached.importMessage"),
    };
  }
  return {
    title: t("contacts.errors.limitReached.title"),
    message: t("contacts.errors.limitReached.message"),
  };
}

/** True only when a capacity preview has definitively rejected the import. */
export function contactImportExecutionBlockedByCapacity(
  capacity: { fitsContactLimit: boolean } | null,
): boolean {
  return capacity?.fitsContactLimit === false;
}

type ClassifiedImportCapacity = {
  contactsRequiredByImport: number;
  fitsContactLimit: boolean;
  /** Null when the organization limit is Unlimited. */
  remainingCapacity: number | null;
  contactsLimit: number | null;
};

/**
 * Zero quota-consuming rows means nothing to create or restore.
 * Duplicate rows are skipped; this import path does not update existing contacts.
 * Raw file size is not an input.
 */
export function contactImportNothingToImport(
  capacity: { contactsRequiredByImport: number } | null,
  classificationPending: boolean,
): boolean {
  if (classificationPending || capacity == null) return false;
  return capacity.contactsRequiredByImport === 0;
}

/**
 * Quota block only after classification, and only when the classified
 * create/restore count exceeds remaining capacity. Raw file size is not an input.
 */
export function contactImportQuotaExceeded(
  capacity: ClassifiedImportCapacity | null,
  classificationPending: boolean,
): boolean {
  if (classificationPending || capacity == null) return false;
  if (capacity.contactsLimit == null || capacity.remainingCapacity == null) return false;
  return (
    capacity.fitsContactLimit === false &&
    capacity.contactsRequiredByImport > capacity.remainingCapacity
  );
}

export function contactImportCanExecute(
  capacity: ClassifiedImportCapacity | null,
  classificationPending: boolean,
): boolean {
  if (classificationPending || capacity == null) return false;
  if (contactImportNothingToImport(capacity, false)) return false;
  if (contactImportQuotaExceeded(capacity, false)) return false;
  if (capacity.contactsLimit == null || capacity.remainingCapacity == null) {
    return capacity.fitsContactLimit;
  }
  return capacity.contactsRequiredByImport <= capacity.remainingCapacity;
}

/**
 * Result cards count rows the worker actually processed.
 * A file-level quota rejection happens before that, so the cards stay at zero.
 */
export function importJobShowsResultCounters(job: {
  status: string;
  errorCode?: string | null;
}): boolean {
  return !(job.status === "FAILED" && job.errorCode === "CONTACT_LIMIT_REACHED");
}

export function mapContactError(t: TFunction, error: unknown, fallback: string): string {
  if (isApiError(error) && error.status === 0) return fallback;
  const code = isApiError(error)
    ? error.code
    : error && typeof error === "object" && "code" in error
      ? String((error as { code?: string }).code ?? "")
      : undefined;
  const key = code ? CONTACT_ERROR_KEYS[code] : undefined;
  if (key) return t(key);
  return fallback;
}

const IMPORT_MESSAGE_KEYS: Record<string, string> = {
  missing_country: "contacts.import.messages.missingCountry",
  invalid_phone: "contacts.import.messages.invalidPhone",
  no_identity: "contacts.import.messages.noIdentity",
  invalid_email: "contacts.import.messages.invalidEmail",
  invalid_birthday: "contacts.import.messages.invalidBirthday",
  birthday_skipped: "contacts.import.messages.birthdaySkipped",
  duplicate_phone: "contacts.import.messages.duplicatePhone",
  duplicate_email: "contacts.import.messages.duplicateEmail",
  whatsapp_exact_match: "contacts.import.messages.whatsappExactMatch",
  ambiguous_match: "contacts.import.messages.ambiguousMatch",
  restore_deleted_contact: "contacts.import.messages.restoreDeleted",
  ambiguous_deleted_phone: "contacts.import.messages.ambiguousDeletedPhone",
  failed_create: "contacts.import.messages.failedCreate",
  duplicate_contact: "contacts.import.messages.duplicatePhone",
  invalid: "contacts.import.messages.invalidPhone",
};

export function mapImportRowMessage(
  t: TFunction,
  input: {
    messageCode?: string | null;
    warningCodes?: readonly string[] | null;
    status?: string;
    message?: string | null;
  },
): string | null {
  const code = input.messageCode ?? input.warningCodes?.[0] ?? input.status;
  const key = code ? IMPORT_MESSAGE_KEYS[code] : undefined;
  if (key) return t(key);
  return null;
}
